'use client';

/**
 * RouteMapThumb — a drawn Australia with one or more routes on it.
 *
 *  • tone="inset": the small map badge on a popular-trip photo (where the
 *    trip goes, at a glance).
 *  • tone="fallback": fills a card's photo slot when the photo is missing or
 *    fails, so the card still says something true.
 *  • tone="strip": the phone empty state's map strip — every shelf route.
 */

import React, { useMemo } from 'react';
import { arcPath, australiaPath, makeProjector, routeEnds, type LngLat } from './auMap';

interface RouteLike {
  id?: string;
  from?: string;
  to?: string;
}

interface RouteMapThumbProps {
  routes: RouteLike[];
  tone: 'inset' | 'fallback' | 'strip';
  className?: string;
  /** strip: the route to draw strongest (others stay faint). */
  highlightId?: string | null;
}

// One projector for every thumbnail: the SVG viewBox scales it.
const VIEW_W = 200;
const project = makeProjector(VIEW_W);
const LAND = australiaPath(project);

const TONES = {
  inset: { sea: 'transparent', land: '#e7e5e4', coast: '#a8a29e', route: '#1558b0', width: 15, dot: 13 },
  fallback: { sea: '#eaf2fb', land: '#ffffff', coast: '#b6c7dc', route: '#1558b0', width: 4, dot: 5 },
  strip: { sea: 'transparent', land: '#ffffff', coast: '#b6c7dc', route: '#1558b0', width: 2.6, dot: 3.4 },
} as const;

export default function RouteMapThumb({ routes, tone, className = '', highlightId = null }: RouteMapThumbProps) {
  const t = TONES[tone];
  const drawn = useMemo(
    () =>
      routes
        .map((r) => ({ id: r.id, ends: routeEnds(r) }))
        .filter((r): r is { id: string | undefined; ends: [LngLat, LngLat] } => r.ends !== null)
        .map((r) => {
          const a = project(r.ends[0]);
          const b = project(r.ends[1]);
          return { id: r.id, a, b, d: arcPath(a, b) };
        }),
    [routes]
  );
  const pad = tone === 'fallback' ? 18 : 4;
  const viewBox = `${-pad} ${-pad} ${VIEW_W + pad * 2} ${project.height + pad * 2}`;

  return (
    <svg
      viewBox={viewBox}
      preserveAspectRatio="xMidYMid meet"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      {t.sea !== 'transparent' && (
        <rect x={-pad} y={-pad} width={VIEW_W + pad * 2} height={project.height + pad * 2} fill={t.sea} />
      )}
      <path d={LAND} fill={t.land} stroke={t.coast} strokeWidth={tone === 'inset' ? 2.5 : 1.2} strokeLinejoin="round" />
      {drawn.map((r, i) => {
        const strong = tone !== 'strip' || highlightId === null || highlightId === r.id;
        return (
          <g key={r.id ?? i} opacity={strong ? 1 : 0.35}>
            <path
              d={r.d}
              fill="none"
              stroke={t.route}
              strokeWidth={t.width}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle cx={r.a[0]} cy={r.a[1]} r={t.dot} fill="#fff" stroke={t.route} strokeWidth={t.width * 0.6} />
            <circle cx={r.b[0]} cy={r.b[1]} r={t.dot} fill={t.route} />
          </g>
        );
      })}
    </svg>
  );
}
